/**
 * Shared hook for managing unit trait state between UnitDetail and UnitInfoDialog
 */

import { useState, useEffect, useMemo } from "react";
import type { Unit } from "@/lib/types/unit";
import type { Veterancy } from "@/lib/types/veterancy-cohesion";
import { 
  VETERANCIES, 
  ARTY_VETERANCIES, 
  PLANE_VETERANCIES, 
  HELICOPTER_VETERANCIES, 
  SF_VETERANCIES,
  ExperiencePackDescriptor,
} from "@/lib/types/veterancy-cohesion";
import { calculateModifiedUnitStats } from "@/lib/utils/unit-stat-modifiers";
import { useUnitTraitEffectsData, useDataStore } from "@/lib/store/data-store";
import { calculateTerrainSpeeds } from "@/lib/utils/terrain-speed-calculator";

/**
 * Get veterancy options based on unit's xpBonuses
 */
function getVeterancyOptions(unit: Unit | undefined): Veterancy[] {
  if (!unit) {
    return VETERANCIES;
  }

  const xpBonuses = unit.xpBonuses;

  if (xpBonuses.includes(ExperiencePackDescriptor.SF)) {
    return SF_VETERANCIES;
  } else if (xpBonuses.includes(ExperiencePackDescriptor.ARTILLERY)) {
    return ARTY_VETERANCIES;
  } else if (xpBonuses.includes(ExperiencePackDescriptor.PLANE)) {
    return PLANE_VETERANCIES;
  } else if (xpBonuses.includes(ExperiencePackDescriptor.HELICOPTER)) {
    return HELICOPTER_VETERANCIES;
  } else {
    return VETERANCIES;
  }
}

export function useUnitTraitState(unit: Unit | undefined) {
  const [traitOverrides, setTraitOverrides] = useState<Map<string, boolean>>(new Map());
  const [veterancy, setVeterancy] = useState<Veterancy | undefined>(undefined);
  const { loadingUnitTraitEffects, unitTraitEffects } = useUnitTraitEffectsData();
  const { terrainSpeedModifiers } = useDataStore();

  // Reset when unit changes
  useEffect(() => {
    // Initialize all traits to explicitly false to prevent auto-activation
    const initialOverrides = new Map<string, boolean>();
    if (unit?.specialities) {
      for (const trait of unit.specialities) {
        if (trait.startsWith('_')) {
          initialOverrides.set(trait, false);
        }
      }
    }
    setTraitOverrides(initialOverrides);
    setVeterancy(undefined);
  }, [unit?.descriptorName, unit?.specialities]);

  // Get all traits that the unit has (starting with '_')
  const traitsWithModifiers = useMemo(() => {
    if (!unit || loadingUnitTraitEffects) return [];
    return (unit.specialities || [])
      .filter(trait => trait.startsWith('_'));
  }, [unit, loadingUnitTraitEffects]);

  // Calculate actual activation state for each trait (for UI display)
  // DEFAULT TO OFF - user must manually toggle traits on
  const traitActivationStates = useMemo(() => {
    if (!unit || loadingUnitTraitEffects) return new Map<string, boolean>();
    const states = new Map<string, boolean>();

    for (const traitName of traitsWithModifiers) {
      // If there's an explicit override, use it
      const override = traitOverrides.get(traitName);
      if (override !== undefined) {
        states.set(traitName, override);
      } else {
        // No override - default to OFF
        states.set(traitName, false);
      }
    }
    
    return states;
  }, [unit, traitOverrides, traitsWithModifiers, loadingUnitTraitEffects]);

  // Calculate modified stats
  const modifiedStats = useMemo(() => {
    if (!unit || loadingUnitTraitEffects) return undefined;
    try {
      return calculateModifiedUnitStats(unit, traitOverrides, veterancy);
    } catch {
      return undefined;
    }
  }, [unit, traitOverrides, veterancy, loadingUnitTraitEffects]);

  // Calculate terrain speeds
  const terrainSpeeds = useMemo(() => {
    if (!unit || !terrainSpeedModifiers) return undefined;
    const baseSpeed = unit.speed;
    const modifiedSpeed = modifiedStats?.speed.isModified ? modifiedStats.speed.modified : undefined;
    return calculateTerrainSpeeds(
      unit, 
      terrainSpeedModifiers, 
      traitOverrides, 
      unitTraitEffects || undefined,
      baseSpeed,
      modifiedSpeed
    );
  }, [unit, terrainSpeedModifiers, traitOverrides, unitTraitEffects, modifiedStats]);

  // Get veterancy options for this unit
  const veterancyOptions = useMemo(() => getVeterancyOptions(unit), [unit]);

  // Handle trait toggle - simple toggle between on/off
  const handleTraitToggle = (traitName: string) => {
    if (!unit) return;
    
    setTraitOverrides(prev => {
      const newMap = new Map(prev);
      const currentValue = newMap.get(traitName);
      
      // Toggle: if currently has override, flip it; if no override (defaults to false), set to true
      if (currentValue !== undefined) {
        newMap.set(traitName, !currentValue);
      } else {
        // No override yet, default is false, so set to true
        newMap.set(traitName, true);
      }
      return newMap;
    });
  };

  return {
    traitOverrides,
    veterancy,
    setVeterancy,
    traitsWithModifiers,
    traitActivationStates,
    modifiedStats,
    terrainSpeeds,
    veterancyOptions,
    handleTraitToggle,
    loadingUnitTraitEffects,
  };
}

